import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ArrowLeft, ArrowRight, CheckCircle2, Download, Loader2 } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import SapSubNav from '@/components/sap/SapSubNav';
import { generateWhitepaper } from '@/components/sap/shared';

const API = process.env.REACT_APP_BACKEND_URL;

const SapWhitepaperPage = () => {
  const [form, setForm] = useState({ name: '', email: '', company: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) { setError('All fields are required'); return; }
    setSaving(true); setError('');
    try {
      const res = await fetch(`${API}/api/leads/sap-assessment`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, source: 'whitepaper', resource_type: 'whitepaper' }) });
      if (!res.ok) { const data = await res.json().catch(() => ({})); setError(data.detail || 'Something went wrong'); return; }
      setSubmitted(true);
      generateWhitepaper(form.name);
    } catch { setError('Network error.'); }
    finally { setSaving(false); }
  };

  const CONTENTS = [
    'Executive summary and market analysis',
    'Risk assessment framework for S/4 migrations',
    'Competitive landscape breakdown (4 market approaches)',
    'TCO model and ROI projections',
    'Actionable recommendations by stakeholder role',
    'Implementation timeline and deployment approach',
  ];

  return (
    <PageLayout>
      <SapSubNav />
      <section className="pb-8 pt-6" data-testid="whitepaper-hero">
        <div className="max-w-5xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-taxera-teal/10 text-taxera-teal text-sm font-medium rounded-full mb-4">
            <FileText className="w-4 h-4" strokeWidth={1.5} /> Whitepaper
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-taxera-charcoal leading-[1.1] mb-4">
            Tax Compliance in the Age of <span className="taxera-gradient-text">SAP S/4HANA</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl">The definitive guide for Tax Directors, CIOs, and CFOs navigating ERP transformation and regulatory compliance.</p>
        </div>
      </section>

      <section className="pb-20" data-testid="whitepaper-body">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-[1fr_400px] gap-8">
            {/* Content preview */}
            <div>
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 mb-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-20 bg-white/10 rounded-xl flex items-center justify-center">
                    <FileText className="w-8 h-8 text-taxera-teal" strokeWidth={1} />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-taxera-teal uppercase tracking-wide mb-1">Taxera Whitepaper 2026</div>
                    <div className="text-lg font-bold text-white">Tax Compliance in the Age of SAP S/4HANA</div>
                  </div>
                </div>
                <div className="text-2xs font-bold text-slate-400 uppercase tracking-wide mb-3">What's inside</div>
                <ul className="space-y-2.5">
                  {CONTENTS.map(c => (
                    <li key={c} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-taxera-teal shrink-0 mt-0.5" /> {c}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6">
                <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-3">Who this is for</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {['Head of Tax / VP Tax', 'CIO / VP IT', 'CFO / Finance Director'].map(role => (
                    <div key={role} className="bg-white rounded-xl p-4 border border-slate-200">
                      <div className="text-sm font-bold text-taxera-charcoal">{role}</div>
                      <p className="text-xs text-slate-500 mt-1">Strategic insights for your S/4HANA migration compliance workstream.</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Download form */}
            <div className="lg:sticky lg:top-32 self-start">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6" data-testid="whitepaper-form">
                {submitted ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-10 h-10 text-taxera-teal mx-auto mb-3" />
                    <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-2">Your whitepaper is ready</h3>
                    <p className="text-sm text-slate-500 mb-4">A print dialog should have opened. Save as PDF to keep your copy.</p>
                    <button onClick={() => generateWhitepaper(form.name)} className="inline-flex items-center gap-2 px-5 py-2.5 bg-taxera-teal text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all" data-testid="whitepaper-redownload">
                      <Download className="w-3.5 h-3.5" /> Download Again
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-heading text-lg font-bold text-taxera-charcoal mb-1">Download the whitepaper</h3>
                    <p className="text-sm text-slate-500 mb-5">Fill in your details to get instant access.</p>
                    <form onSubmit={handleSubmit} className="space-y-3">
                      <input type="text" placeholder="Full name" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white focus:border-taxera-blue focus:ring-1 focus:ring-taxera-blue outline-none transition-all" data-testid="wp-name-input" />
                      <input type="email" placeholder="Work email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white focus:border-taxera-blue focus:ring-1 focus:ring-taxera-blue outline-none transition-all" data-testid="wp-email-input" />
                      <input type="text" placeholder="Company" value={form.company} onChange={e => setForm(p => ({ ...p, company: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm bg-white focus:border-taxera-blue focus:ring-1 focus:ring-taxera-blue outline-none transition-all" data-testid="wp-company-input" />
                      {error && <p className="text-xs text-red-500">{error}</p>}
                      <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 taxera-gradient text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50" data-testid="wp-submit-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} {saving ? 'Processing...' : 'Download Now'}
                      </button>
                      <p className="text-2xs text-slate-400 text-center">No spam. Your data is safe.</p>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default SapWhitepaperPage;
